/**
 * RUMIO Jackpot Celebration Component
 *
 * A dramatic celebration overlay for when a player wins the RUMIO jackpot
 * (R-U-M-I-O letters across all 5 columns = jackpot!)
 *
 * Features:
 * - Epic celebration animation
 * - Pro+ subscription award notification
 * - Rumio payout display
 * - Confetti and particle effects
 */
/* eslint-disable rumio-i18n/no-hardcoded-strings */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// =============================================================================
// Types
// =============================================================================

interface RumioJackpotCelebrationProps {
  /** Whether the celebration is active */
  isActive: boolean;
  /** The jackpot prize info */
  jackpot: {
    payout: number;
    months: number;
    row: number | null;
  } | null;
  /** Called when celebration is dismissed */
  onClose: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

export function RumioJackpotCelebration({
  isActive,
  jackpot,
  onClose,
}: RumioJackpotCelebrationProps) {
  const [phase, setPhase] = useState<'letters' | 'prize' | 'subscription'>('letters');
  const [showCollect, setShowCollect] = useState(false);

  // Progress through celebration phases
  useEffect(() => {
    if (!isActive) {
      // Schedule state updates to avoid cascading renders
      requestAnimationFrame(() => {
        setPhase('letters');
        setShowCollect(false);
      });
      return undefined;
    }

    // Phase timing
    const lettersTimer = setTimeout(() => setPhase('prize'), 3000);
    const subscriptionTimer = setTimeout(() => setPhase('subscription'), 5000);
    const collectTimer = setTimeout(() => setShowCollect(true), 7000);

    return () => {
      clearTimeout(lettersTimer);
      clearTimeout(subscriptionTimer);
      clearTimeout(collectTimer);
    };
  }, [isActive]);

  const handleCollect = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isActive || !jackpot) return null;

  const subscriptionText = getSubscriptionText(jackpot.months);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md"
    >
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Spinning rays */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <RaysBurst />
        </motion.div>

        {/* Particle confetti */}
        <JackpotConfetti />
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center">
        <AnimatePresence mode="wait">
          {/* Phase 1: RUMIO Letters Animation */}
          {phase === 'letters' && (
            <motion.div
              key="letters"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="space-y-6"
            >
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.5, repeat: Infinity }}
                className="text-6xl font-bold tracking-widest"
              >
                <span className="text-red-500">R</span>
                <span className="text-orange-500">U</span>
                <span className="text-yellow-500">M</span>
                <span className="text-green-500">I</span>
                <span className="text-blue-500">O</span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-4xl font-bold text-yellow-400"
              >
                JACKPOT!
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="text-xl text-white/80"
              >
                You got all 5 letters!
              </motion.div>
            </motion.div>
          )}

          {/* Phase 2: Prize Amount */}
          {phase === 'prize' && (
            <motion.div
              key="prize"
              initial={{ opacity: 0, scale: 0.5, rotateY: 90 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="space-y-6"
            >
              <div className="text-2xl text-white/80">You Won</div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.3, 1] }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-7xl font-bold text-yellow-400 drop-shadow-lg"
              >
                +{jackpot.payout.toLocaleString()}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-3xl font-bold text-yellow-200"
              >
                RUMIOS!
              </motion.div>
            </motion.div>
          )}

          {/* Phase 3: Pro+ Subscription */}
          {phase === 'subscription' && (
            <motion.div
              key="subscription"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="space-y-6"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5, repeat: Infinity }}
                className="text-6xl"
              >
                👑
              </motion.div>

              <div className="text-xl text-white/80">AND</div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                className="space-y-2"
              >
                <div className="text-3xl font-bold text-yellow-400">
                  FREE Pro+ Membership!
                </div>
                <div className="text-5xl font-bold text-white">
                  {subscriptionText}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col items-center gap-2 text-lg text-white/70"
              >
                <span>Unlimited searches</span>
                <span>Priority support</span>
                <span>Exclusive features</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Collect Button */}
        <AnimatePresence>
          {showCollect && (
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={handleCollect}
              className="mt-10 px-12 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold text-xl rounded-full hover:from-yellow-400 hover:to-orange-400 transition-colors shadow-lg shadow-yellow-500/30"
            >
              COLLECT YOUR PRIZE!
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// =============================================================================
// Helper Functions
// =============================================================================

function getSubscriptionText(months: number): string {
  if (months === 12) return '1 YEAR';
  if (months === 1) return '1 MONTH';
  return `${months} MONTHS`;
}

// =============================================================================
// Visual Components
// =============================================================================

function RaysBurst() {
  const rays = Array.from({ length: 12 });

  return (
    <div className="relative w-[800px] h-[800px]">
      {rays.map((_, i) => (
        <div
          key={i}
          className="absolute top-1/2 left-1/2 w-[2px] h-[400px] origin-bottom"
          style={{
            transform: `translateX(-50%) rotate(${i * 30}deg)`,
            background: `linear-gradient(to top, transparent, ${
              i % 2 === 0 ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 165, 0, 0.2)'
            })`,
          }}
        />
      ))}
    </div>
  );
}

function JackpotConfetti() {
  const particles = Array.from({ length: 100 });

  // Pre-generate random values for all particles once on mount
  const [particleData] = useState(() => {
    const generateRandom = () => Math.random();
    return particles.map((_, i) => ({
      xTarget: generateRandom() * 100,
      yTarget: generateRandom() * 100,
      scale: generateRandom() * 0.8 + 0.4,
      rotate: generateRandom() * 720 - 360,
      duration: 3 + generateRandom() * 2,
      delay: generateRandom() * 2,
      repeatDelay: generateRandom() * 2,
      color: getParticleColor(i),
      borderRadius: i % 3 === 0 ? '50%' : i % 3 === 1 ? '2px' : '0',
    }));
  });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((_, i) => {
        const data = particleData[i];
        return (
          <motion.div
            key={i}
            initial={{
              opacity: 0,
              x: '50%',
              y: '50%',
              scale: 0,
            }}
            animate={{
              opacity: [0, 1, 1, 0],
              x: `${data.xTarget}%`,
              y: `${data.yTarget}%`,
              scale: [0, data.scale, 0],
              rotate: data.rotate,
            }}
            transition={{
              duration: data.duration,
              delay: data.delay,
              repeat: Infinity,
              repeatDelay: data.repeatDelay,
              ease: 'easeOut',
            }}
            className="absolute w-4 h-4"
            style={{
              backgroundColor: data.color,
              borderRadius: data.borderRadius,
              boxShadow: `0 0 10px ${data.color}`,
            }}
          />
        );
      })}
    </div>
  );
}

function getParticleColor(index: number): string {
  const colors = [
    '#FFD700', // Gold
    '#FFA500', // Orange
    '#FF6347', // Tomato
    '#FFB6C1', // Light Pink
    '#87CEEB', // Sky Blue
    '#98FB98', // Pale Green
    '#DDA0DD', // Plum
    '#F0E68C', // Khaki
  ];
  return colors[index % colors.length];
}

export default RumioJackpotCelebration;
